import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import StoryCardList from '../../containers/StoryCardList';
import './HomeView.css';
import { useStoriesCollection } from '../../contexts/StoryContext';
import { useCharacters } from '../../contexts/CharacterContext';
import { useAuth } from '../../contexts/AuthContext';
import { Button, LoadingIndicator } from '../../components/ui';
import WelcomeBackBanner from '../../components/WelcomeBackBanner/WelcomeBackBanner';

/**
 * HomeView component
 * Main landing page showing available stories and option to create new ones
 * Acts as a container for StoryCardList, managing data fetching and view states
 * 
 * @param {Object} props - Component props
 * @param {function} props.onStorySelect - Called when a story is selected
 * @param {function} props.onCreateNewStory - Called when new story button is clicked
 */
const HomeView = ({ onStorySelect, onCreateNewStory }) => {
  const [isFirstRun, setIsFirstRun] = useState(false);
  const navigate = useNavigate();
  
  // Use the stories collection from StoryContext
  const { 
    stories, 
    isLoading: storiesLoading, 
    error: storiesError, 
    fetchUserStories 
  } = useStoriesCollection();
  
  // Use characters from CharacterContext
  const { characters, isLoading: charactersLoading } = useCharacters();
  
  // Use auth context to get authentication state and active child profile
  const { activeChildProfile, isAuthenticated } = useAuth();
  
  // Determine when both stories and characters are loading
  const isLoading = storiesLoading || charactersLoading;

  // We need to include fetchUserStories in the dependency array
  // but we need to prevent re-fetching when it changes
  const initialMountRef = useRef(true);
  
  useEffect(() => {
    // Only fetch on initial mount
    if (initialMountRef.current) {
      console.log('[HomeView] Initial mount, fetching stories');
      initialMountRef.current = false;
      
      fetchUserStories().catch(err => {
        console.error('[HomeView] Error fetching stories:', err);
      });
    }
  }, [fetchUserStories]); // Include fetchUserStories to satisfy the linter, but use ref to control execution

  // Determine first run status after data is loaded
  useEffect(() => {
    // Skip during loading
    if (isLoading) return;
    
    // Check if there are characters or stories
    const hasCharacters = characters && characters.length > 0;
    const hasStories = stories && stories.length > 0;
    
    // If there are no characters or stories, show first run experience
    const shouldShowFirstRun = !hasCharacters && !hasStories;
    console.log('[HomeView] First run detection:', {
      hasCharacters,
      charactersLength: characters?.length,
      hasStories,
      storiesLength: stories?.length,
      shouldShowFirstRun,
      currentIsFirstRun: isFirstRun
    });
    
    // Only update state if it actually changed to avoid infinite re-rendering
    if (shouldShowFirstRun !== isFirstRun) {
      setIsFirstRun(shouldShowFirstRun);
    }
  }, [characters, stories, isLoading]); // Removed isFirstRun from dependencies

  // Show loading state while data is being fetched
  if (isLoading) {
    return (
      <div className="home-view home-view--loading" data-testid="home-view-loading">
        <LoadingIndicator size="large" label="Loading..." />
      </div>
    );
  }

  // Show first-run experience if no stories or characters
  if (isFirstRun) {
    return (
      <div className="home-view home-view--first-run" data-testid="home-view-first-run">
        <header className="home-view__header">
          <h1 className="home-view__title">Welcome to Weaver's World!</h1>
          <p className="home-view__subtitle">
            Get started creating magical interactive stories for children
          </p>
        </header>
        
        <div className="home-view__first-run-content">
          <div className="home-view__welcome-card">
            <h2>Create Your Own Magical Stories!</h2>
            <p>
              Weaver's World lets you create unique stories featuring characters you design.
              Each story is illustrated with beautiful images that bring your tale to life.
            </p>
            
            {!isAuthenticated && (
              <p>
                Create a free account to get started with your storytelling journey.
              </p>
            )}
            
            <div className="home-view__welcome-steps">
              <div className="home-view__welcome-step">
                <div className="home-view__step-number">1</div>
                <div className="home-view__step-content">
                  <h3>Create Your Characters</h3>
                  <p>Design characters with unique personalities and traits</p>
                </div>
              </div>
              
              <div className="home-view__welcome-step">
                <div className="home-view__step-number">2</div>
                <div className="home-view__step-content">
                  <h3>Set Your Story Theme</h3>
                  <p>Choose a theme and desired ending for your story</p>
                </div>
              </div>
              
              <div className="home-view__welcome-step">
                <div className="home-view__step-number">3</div>
                <div className="home-view__step-content">
                  <h3>Generate Story Pages</h3>
                  <p>Watch as your story unfolds with text and images</p>
                </div>
              </div>
            </div>
            
            {isAuthenticated ? (
              /* For authenticated users, go directly to create child profile or story */
              <Button 
                className="home-view__get-started-button"
                onClick={() => {
                  // If user has no profiles, go to profile creation first
                  if (!activeChildProfile) {
                    // Pass state to indicate we're coming from first-run experience
                    navigate('/child-profiles/create', { state: { fromFirstRun: true } });
                  } else {
                    // Otherwise go directly to story creation
                    navigate('/weaversworld/create');
                  }
                }}
                data-testid="get-started-button"
              >
                Get Started
              </Button>
            ) : (
              /* For unauthenticated users, go to signup */
              <Button 
                className="home-view__get-started-button"
                onClick={() => navigate('/accounts/signup')}
                data-testid="get-started-button"
              >
                Sign Up to Get Started
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Show regular view with stories
  return (
    <div className="home-view" data-testid="home-view-regular">
      <header className="home-view__header">
        <h1 className="home-view__title">Weaver's World</h1>
        <p className="home-view__subtitle">Create and explore interactive stories</p>
      </header>
      
      <WelcomeBackBanner />
      
      <main className="home-view__content">
        <h2 className="home-view__section-title">{activeChildProfile ? `${activeChildProfile.name}'s Stories` : "Your Stories"}</h2>
        {/* Pass stories data down to StoryCardList */}
        <StoryCardList 
          stories={stories}
          isLoading={storiesLoading}
          error={storiesError}
          onStorySelect={onStorySelect}
          onNewStory={onCreateNewStory}
        />
      </main>
    </div>
  );
};

export default HomeView;